import { getApiRequest, postApiRequest } from "@/services/api-request";

export interface OidcProvider {
  id: string;
  label: string;
}

export interface OidcPrintHouse {
  userId?: string;
  printHouseId: string;
  printHouseName?: string;
  domain?: string;
  secondaryDomain?: string;
  roleId?: string;
  isActive?: boolean;
}

export interface OidcCallbackPayload {
  token?: string;
  refreshToken?: string;
  idToken?: string;
  expiresIn?: number;
  isTermsAccepted?: boolean;
  oidcUserId?: string;
  email?: string;
  cognitoSub?: string;
  selectedPrintHouseId?: string;
  selectedDomain?: string;
  needsPrintHouseSelection?: boolean;
  printHouses?: OidcPrintHouse[];
  returnUrl?: string;
  /** The host the user started on (e.g. pix.gomake.net) before being sent to Cognito. */
  originHost?: string;
  inviteToken?: string;
}

/** Hours the browser clock is ahead of UTC. Positive is east of UTC (e.g. +3), negative is west (e.g. -2). */
export const getTimeZoneOffsetHours = (): number => {
  const offsetMinutes = new Date().getTimezoneOffset() * -1;
  return offsetMinutes / 60;
};

/** Lists the OIDC providers enabled on the backend (GoMake / Google / HP / ...). */
export const fetchOidcProviders = async (): Promise<OidcProvider[]> => {
  const res = await getApiRequest("/v1/auth/oidc/providers", {});
  if (res?.success) {
    return res.data?.data?.providers ?? [];
  }
  return [];
};

/**
 * Localhost callback only. Cognito allows `http://localhost:3004/auth/oidc/callback`
 * and `https://gomake.net/auth/oidc/callback`, so a staging or print-house origin
 * sent as redirect_uri is rejected with redirect_mismatch.
 * Deployed hosts omit this and use the backend `oidc_redirect_uri`. The host the
 * user started on still travels in `originHost` and is handed back after login.
 */
export const getOidcRedirectUri = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname;
  if (host !== "localhost" && host !== "127.0.0.1" && host !== "::1") {
    return undefined;
  }
  return `${window.location.origin}/auth/oidc/callback`;
};

/** Requests a Cognito Hosted UI authorize URL. Omit `provider` for the generic Hosted UI. */
export const startOidcLogin = async (params: {
  /** Omit (or "hosted") to open the generic Cognito Hosted UI showing all providers. */
  provider?: string;
  inviteToken?: string;
  redirectUri?: string;
  returnUrl?: string;
  printHouseId?: string;
  /** Host the user started on (e.g. pix.gomake.net), so we can return them there post-login. */
  originHost?: string;
  /** New invitee: native GoMake opens the Cognito Hosted UI sign-up screen. */
  signup?: boolean;
}): Promise<string | null> => {
  const query: Record<string, string | number> = {
    timeZoneOffset: getTimeZoneOffsetHours(),
  };
  if (params.provider) query.provider = params.provider;
  if (params.inviteToken) query.inviteToken = params.inviteToken;
  const redirectUri = params.redirectUri || getOidcRedirectUri();
  if (redirectUri) query.redirectUri = redirectUri;
  if (params.returnUrl) query.returnUrl = params.returnUrl;
  if (params.printHouseId) query.printHouseId = params.printHouseId;
  if (params.originHost) query.originHost = params.originHost;
  if (params.signup) query.flow = "signup";

  const res = await getApiRequest("/v1/auth/oidc/start", query);
  if (res?.success) {
    return res.data?.data?.authorizeUrl ?? null;
  }
  return null;
};

/**
 * Requests the Cognito Hosted UI sign-out URL. It logs the user out of Cognito and immediately
 * re-opens the login screen (via the normal callback), so `originHost` lets the callback route
 * the user back to the right print-house domain after they sign in again.
 */
export const fetchOidcLogoutUrl = async (params?: {
  originHost?: string;
  returnUrl?: string;
}): Promise<string | null> => {
  const query: Record<string, string | number> = {
    timeZoneOffset: getTimeZoneOffsetHours(),
  };
  if (params?.originHost) query.originHost = params.originHost;
  if (params?.returnUrl) query.returnUrl = params.returnUrl;
  const redirectUri = getOidcRedirectUri();
  if (redirectUri) query.redirectUri = redirectUri;

  const res = await getApiRequest("/v1/auth/oidc/logout", query);
  if (res?.success) {
    return res.data?.data?.logoutUrl ?? null;
  }
  return null;
};

export interface OidcInviteInfo {
  found: boolean;
  email?: string;
  isNewUser: boolean;
  printHouseName?: string;
}

/**
 * Resolves an invite token to whether the invited email already has a GoMake login.
 * Used to switch the invite page between "Login with …" and "Sign up with …".
 */
export const fetchOidcInviteInfo = async (
  token: string,
): Promise<OidcInviteInfo> => {
  const res = await getApiRequest("/v1/auth/oidc/invite-info", { token });
  const data = res?.data?.data;
  if (res?.success && data) {
    return {
      found: data.found ?? false,
      email: data.email,
      // Default to sign-up so a new invitee is never blocked on a login screen.
      isNewUser: data.isNewUser ?? true,
      printHouseName: data.printHouseName,
    };
  }
  return { found: false, isNewUser: true };
};

/** Exchanges the Cognito authorization code and resolves the OIDC identity + memberships. */
export const oidcCallback = async (body: {
  code: string;
  state: string;
  timeZoneOffset?: number;
}) => {
  return postApiRequest("/v1/auth/oidc/callback", {
    ...body,
    timeZoneOffset: body.timeZoneOffset ?? getTimeZoneOffsetHours(),
  });
};

/** Switches the active print-house membership and rebuilds the session cache. */
export const oidcSelectPrintHouse = async (body: {
  printHouseId: string;
  cognitoSub?: string;
  timeZoneOffset?: number;
}) => {
  return postApiRequest("/v1/auth/select-print-house", {
    ...body,
    timeZoneOffset: body.timeZoneOffset ?? getTimeZoneOffsetHours(),
  });
};

/**
 * Loads the authenticated user's accessible print houses from the backend on demand. The header
 * switcher uses this because `oidc_print_houses` is only written by the OIDC callback (a single
 * fixed Cognito redirect origin); after a `/token-auth` cross-domain handoff — or when running
 * locally against a remote backend — that list is absent, so we refetch it here.
 */
export const fetchOidcPrintHouses = async (): Promise<OidcPrintHouse[]> => {
  const res = await getApiRequest("/v1/auth/oidc/print-houses", {});
  if (res?.success) {
    const houses: OidcPrintHouse[] = res.data?.data?.printHouses ?? [];
    // Refresh the cache so subsequent renders/navigations have the list immediately.
    storeOidcSession(houses, getStoredOidcCognitoSub());
    return houses;
  }
  return [];
};

/**
 * The OIDC print-house memberships + cognito subject are cached in localStorage after
 * login so the in-app header switcher can offer other print houses without re-authenticating.
 */
const OIDC_PRINT_HOUSES_KEY = "oidc_print_houses";
const OIDC_COGNITO_SUB_KEY = "oidc_cognito_sub";

export const storeOidcSession = (
  printHouses: OidcPrintHouse[],
  cognitoSub?: string,
): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      OIDC_PRINT_HOUSES_KEY,
      JSON.stringify(printHouses ?? []),
    );
    if (cognitoSub) {
      localStorage.setItem(OIDC_COGNITO_SUB_KEY, cognitoSub);
    }
  } catch {
    // ignore storage quota / serialization errors
  }
};

export const getStoredOidcPrintHouses = (): OidcPrintHouse[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(OIDC_PRINT_HOUSES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const getStoredOidcCognitoSub = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  return localStorage.getItem(OIDC_COGNITO_SUB_KEY) || undefined;
};
